import { useCallback, useEffect, useRef, useState } from "react";
import { getMySpaceInteriorCatalogs } from "../../../api/partner/partnerFeatureApis";
import { CATALOG_PAGE_SIZE } from "./catalogUtils";

/**
 * The logged-in partner's own catalogs (POST /interior/catalogs/mySpace).
 * The API returns no totals, so "load more" is shown while a page comes back full.
 */
const useMyCatalogs = ({ token, userId, keyword, typeId }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState("");
  const [hasMore, setHasMore] = useState(false);
  const pageRef = useRef(1);
  const requestRef = useRef(0);

  const fetchPage = useCallback(
    async (page) => {
      const data = await getMySpaceInteriorCatalogs(token, {
        userId,
        page_number: page,
        page_size: CATALOG_PAGE_SIZE,
        keyword_search: keyword,
        type_id: typeId,
      });
      if (data?.statusType && data.statusType !== "SUCCESS") {
        throw new Error(data.text || "Couldn't load your catalogs.");
      }
      return data?.catalogs || [];
    },
    [token, userId, keyword, typeId]
  );

  const reload = useCallback(async () => {
    if (!token || !userId) return;
    const requestId = ++requestRef.current;
    setLoading(true);
    setError("");
    pageRef.current = 1;
    try {
      const list = await fetchPage(1);
      if (requestId !== requestRef.current) return;
      setItems(list);
      setHasMore(list.length === CATALOG_PAGE_SIZE);
    } catch (err) {
      if (requestId !== requestRef.current) return;
      setItems([]);
      setHasMore(false);
      setError(err?.response?.data?.text || err?.message || "Couldn't load your catalogs.");
    } finally {
      if (requestId === requestRef.current) setLoading(false);
    }
  }, [token, userId, fetchPage]);

  useEffect(() => {
    reload();
  }, [reload]);

  const loadMore = useCallback(async () => {
    if (loadingMore || loading || !hasMore) return;
    const requestId = requestRef.current;
    setLoadingMore(true);
    try {
      const nextPage = pageRef.current + 1;
      const list = await fetchPage(nextPage);
      if (requestId !== requestRef.current) return;
      pageRef.current = nextPage;
      // De-duplicate in case rows shifted between pages
      setItems((prev) => {
        const seen = new Set(prev.map((item) => item.id));
        return [...prev, ...list.filter((item) => !seen.has(item.id))];
      });
      setHasMore(list.length === CATALOG_PAGE_SIZE);
    } catch (err) {
      setError(err?.response?.data?.text || err?.message || "Couldn't load more catalogs.");
    } finally {
      setLoadingMore(false);
    }
  }, [fetchPage, hasMore, loading, loadingMore]);

  const removeLocal = useCallback((id) => setItems((prev) => prev.filter((item) => item.id !== id)), []);

  return { items, loading, loadingMore, error, hasMore, reload, loadMore, removeLocal };
};

export default useMyCatalogs;
